import { useCallback, useEffect } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { isApiError } from '@bookora/ui';

import {
  guideClinicSettings,
  guideCreateDraft,
  guideOccasions,
  guideTipAction,
  guideTips,
  guideUpdateClinicSettings,
} from '@/api/generated/guide/guide';
import type {
  GuideClinicSettingsResponse,
  GuideDraftResponse,
  GuideOccasionsResponse,
  GuideTipActionDto,
  GuideTipsResponse,
  UpdateGuidanceProfileDto,
  UpdateGuideClinicSettingsDto,
} from '@/api/generated/model';
import { queryKeys } from '@/api/query-keys';
import { useAuth } from '@/auth/token-store';
import { useUpdateGuidanceProfile } from '@/guidance';
import { toast } from '@/lib/feedback';

/**
 * «پیشنهادها» — the guide's server state, over the generated `guide` client.
 *
 * ── ONE LIST, SHARED BY EVERY SURFACE ───────────────────────────────────────
 *
 * The dashboard card, the hub section, the inline slots and the Help badge all
 * read the same `GET /guide/tips` answer under `queryKeys.guide.tips(clinic)`.
 * The Help entry is mounted on every screen and asks for the badge, so the list
 * is warm before the dashboard needs it. It is cheap on the server (a cached
 * clinic snapshot filtered per user) but not free, so it is not refetched on
 * focus and stays fresh for five minutes; the hub asks for a live re-check
 * (`verifyAll`) when it opens, and every action answers with the fresh list.
 *
 * A clinic switch clears the whole query cache (`clinic-switcher.tsx`), and
 * every key here carries the clinic, so nothing crosses the switch.
 */

const TIPS_STALE_MS = 5 * 60_000;

/** Retries once, and never on a refusal — a 4xx will not change on a retry. */
function retryOnce(failureCount: number, error: unknown): boolean {
  if (isApiError(error) && error.statusCode >= 400 && error.statusCode < 500) return false;
  return failureCount < 1;
}

/** `GET /guide/tips` for the signed-in staff member in the active clinic. */
export function useGuideTipsQuery(): UseQueryResult<GuideTipsResponse, unknown> {
  const { activeClinicId, isAuthenticated } = useAuth();
  return useQuery({
    queryKey: queryKeys.guide.tips(activeClinicId),
    queryFn: ({ signal }) => guideTips(undefined, signal),
    enabled: isAuthenticated && Boolean(activeClinicId),
    staleTime: TIPS_STALE_MS,
    refetchOnWindowFocus: false,
    retry: retryOnce,
  });
}

/**
 * Opening the hub re-checks every listed tip against the clinic's live data
 * (`verifyAll`), so nothing it lists is already done. The answer replaces the
 * shared list; a failure leaves the cached list on screen.
 */
export function useVerifyTipsOnMount(enabled: boolean): void {
  const queryClient = useQueryClient();
  const { activeClinicId, isAuthenticated } = useAuth();
  useEffect(() => {
    if (!enabled || !isAuthenticated || !activeClinicId) return;
    const controller = new AbortController();
    guideTips({ verifyAll: true }, controller.signal).then(
      (fresh) => {
        if (!controller.signal.aborted) {
          queryClient.setQueryData(queryKeys.guide.tips(activeClinicId), fresh);
        }
      },
      () => undefined,
    );
    return () => controller.abort();
  }, [enabled, isAuthenticated, activeClinicId, queryClient]);
}

export type TipActionVars = GuideTipActionDto;

/** Actions that take the tip off the list the user is looking at. */
const HIDING = new Set<GuideTipActionDto['action']>([
  'SNOOZE_DAY',
  'SNOOZE_WEEK',
  'DISMISS',
  'NOT_RELEVANT',
  'MARK_DONE',
]);

/** The list without one tip — the optimistic state of a hiding action. */
export function withoutTip(data: GuideTipsResponse, itemKey: string): GuideTipsResponse {
  const tips = data.tips.filter((tip) => tip.itemKey !== itemKey);
  const removedNew = data.tips.some((tip) => tip.itemKey === itemKey && tip.isNew);
  return {
    ...data,
    tips,
    later: data.later.filter((tip) => tip.itemKey !== itemKey),
    badgeCount: Math.max(0, data.badgeCount - (removedNew ? 1 : 0)),
  };
}

/**
 * `POST /guide/tips/action`. A hiding action removes the tip at once and puts
 * it back (with the server's reason) if the request fails; the answer is the
 * fresh list, which replaces the cache either way.
 */
export function useGuideTipAction(): UseMutationResult<
  GuideTipsResponse,
  unknown,
  TipActionVars,
  { previous: GuideTipsResponse | undefined }
> {
  const queryClient = useQueryClient();
  const { activeClinicId } = useAuth();
  const key = queryKeys.guide.tips(activeClinicId);
  return useMutation({
    mutationFn: (vars: TipActionVars) => guideTipAction(vars),
    onMutate: async (vars) => {
      const previous = queryClient.getQueryData<GuideTipsResponse>(key);
      if (previous && HIDING.has(vars.action)) {
        await queryClient.cancelQueries({ queryKey: key });
        queryClient.setQueryData(key, withoutTip(previous, vars.itemKey));
      }
      return { previous };
    },
    onError: (error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
      toast.error(error);
    },
    onSuccess: (fresh) => {
      queryClient.setQueryData(key, fresh);
    },
  });
}

/** `POST /guide/tips/draft` — the server's DRAFT campaign for an occasion or win-back tip. */
export function useGuideCreateDraft(): UseMutationResult<GuideDraftResponse, unknown, string> {
  return useMutation({ mutationFn: (itemKey: string) => guideCreateDraft({ itemKey }) });
}

/** Upcoming occasions for the hub (`crm.send_campaigns` holders only). */
export function useGuideOccasionsQuery(
  days: number,
  enabled: boolean,
): UseQueryResult<GuideOccasionsResponse, unknown> {
  const { activeClinicId, isAuthenticated } = useAuth();
  return useQuery({
    queryKey: queryKeys.guide.occasions(activeClinicId, days),
    queryFn: ({ signal }) => guideOccasions({ days }, signal),
    enabled: enabled && isAuthenticated && Boolean(activeClinicId),
    staleTime: 60 * 60_000,
    refetchOnWindowFocus: false,
    retry: retryOnce,
  });
}

/** Clinic-wide switch and hidden rules (`settings.view`). */
export function useGuideClinicSettingsQuery(
  enabled: boolean,
): UseQueryResult<GuideClinicSettingsResponse, unknown> {
  const { activeClinicId, isAuthenticated } = useAuth();
  return useQuery({
    queryKey: queryKeys.guide.clinicSettings(activeClinicId),
    queryFn: ({ signal }) => guideClinicSettings(signal),
    enabled: enabled && isAuthenticated && Boolean(activeClinicId),
    retry: retryOnce,
  });
}

/** `PUT /guide/clinic-settings` (`settings.edit`); the tips are re-read afterwards. */
export function useUpdateGuideClinicSettings(): UseMutationResult<
  GuideClinicSettingsResponse,
  unknown,
  UpdateGuideClinicSettingsDto
> {
  const queryClient = useQueryClient();
  const { activeClinicId } = useAuth();
  return useMutation({
    mutationFn: (dto: UpdateGuideClinicSettingsDto) => guideUpdateClinicSettings(dto),
    onSuccess: (fresh) => {
      queryClient.setQueryData(queryKeys.guide.clinicSettings(activeClinicId), fresh);
      void queryClient.invalidateQueries({ queryKey: queryKeys.guide.tips(activeClinicId) });
    },
    onError: (error) => toast.error(error),
  });
}

/**
 * The caller's own tip preferences — pause, off, muted categories — live on the
 * guidance profile (`PATCH /guidance/profile`), shared with the tours. The
 * tours' own mutation (`useUpdateGuidanceProfile`) keeps the guidance state
 * cache right; the tips are re-read afterwards, since pausing or muting changes
 * what the server lists as proactive.
 */
export function useUpdateTipPreferences(): {
  mutate: (dto: UpdateGuidanceProfileDto) => void;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const { activeClinicId } = useAuth();
  const update = useUpdateGuidanceProfile();
  const { mutate } = update;
  const run = useCallback(
    (dto: UpdateGuidanceProfileDto) =>
      mutate(dto, {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: queryKeys.guide.tips(activeClinicId) });
        },
      }),
    [mutate, queryClient, activeClinicId],
  );
  return { mutate: run, isPending: update.isPending };
}

/** Re-read the list (after a stale draft or a refusal). */
export function useRefetchTips(): () => void {
  const queryClient = useQueryClient();
  const { activeClinicId } = useAuth();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.guide.tips(activeClinicId) });
  };
}

/** The hub section is drawn unless the guide is switched off for the whole deployment. */
export function useTipsSectionVisible(): boolean {
  const query = useGuideTipsQuery();
  return query.data?.quietReason !== 'DISABLED';
}

/**
 * The Help entry's dot and «راهنما، n پیشنهاد تازه»: new tips, but only while
 * the guide may be proactive for this person — a paused or switched-off guide
 * does not wave at them from the header.
 */
export function useTipsBadgeCount(): number {
  const data = useGuideTipsQuery().data;
  return data && data.proactive && data.badgeCount > 0 ? data.badgeCount : 0;
}
